import { Box, Container, Typography, Grid, Stack, Chip } from '@mui/material';
import {
  Sparkles,
  BarChart3,
  GitBranch,
  Users,
  Check,
  UserCog,
  MonitorPlay,
  MessageSquareText,
} from 'lucide-react';
import Reveal from './Reveal';
import { tokens } from '../theme';

/**
 * Owner command-center section (TASK K): CleoleadAI assistant, AI Analyst
 * (incl. self-learning prompt suggestions), conversation flow graph, client
 * profiles with Trends — plus a compact strip for team/demo/widget.
 */

const SAGE_BORDER = 'rgba(47, 93, 80, 0.25)';

const cardSx = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  bgcolor: tokens.bgPaper,
  border: `1px solid ${tokens.border}`,
  borderRadius: `${tokens.radiusCard}px`,
  boxShadow: tokens.shadowSoft,
  p: { xs: 3.25, md: 4 },
  transition: `box-shadow ${tokens.motion.micro}ms ease, border-color ${tokens.motion.micro}ms ease`,
  '&:hover': {
    boxShadow: tokens.shadowLift,
    borderColor: '#D9CDBC',
  },
} as const;

const iconSquareSx = {
  width: 44,
  height: 44,
  borderRadius: `${tokens.radiusControl}px`,
  bgcolor: tokens.copperTint,
  color: tokens.copper,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const;

/* ── mini visuals ─────────────────────────────────────────────── */

function HermesChat() {
  const lines = [
    { from: 'you', text: 'How did we do yesterday? Anything I should fix?' },
    {
      from: 'ai',
      text: '14 conversations, 6 bookings. Two guests asked about gluten-free options — your menu doc doesn\'t cover it. Want me to draft an answer?',
    },
    { from: 'you', text: 'Yes — and call the produce supplier about Friday.' },
  ];
  return (
    <Box
      sx={{
        borderRadius: `${tokens.radiusControl}px`,
        border: `1px solid ${tokens.border}`,
        bgcolor: tokens.bgDefault,
        p: 2,
        width: '100%',
        maxWidth: 430,
      }}
    >
      {lines.map((l, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            justifyContent: l.from === 'you' ? 'flex-end' : 'flex-start',
            mb: i < lines.length - 1 ? 1 : 0,
          }}
        >
          <Box
            sx={{
              maxWidth: '88%',
              px: 1.5,
              py: 0.9,
              borderRadius: '10px',
              bgcolor: l.from === 'you' ? tokens.copperTint : tokens.bgPaper,
              border: `1px solid ${l.from === 'you' ? 'rgba(199,91,57,0.25)' : tokens.border}`,
            }}
          >
            <Typography sx={{ fontSize: '0.78rem', color: 'text.primary', lineHeight: 1.5 }}>
              {l.text}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function AnalystBrief() {
  const items = [
    { label: 'Conversation health', value: '92 / 100', tone: 'sage' },
    { label: 'Bookings this week', value: '+18%', tone: 'sage' },
    { label: 'Unanswered topic found', value: 'gift cards', tone: 'copper' },
    { label: 'Prompt suggestion ready', value: '1-click apply', tone: 'copper' },
  ];
  return (
    <Box
      sx={{
        borderRadius: `${tokens.radiusControl}px`,
        border: `1px solid ${tokens.border}`,
        bgcolor: tokens.bgPaper,
        overflow: 'hidden',
        width: '100%',
        maxWidth: 380,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.1,
          bgcolor: tokens.bgDefault,
          borderBottom: `1px solid ${tokens.border}`,
        }}
      >
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary' }}>
          Today's briefing
        </Typography>
      </Box>
      {items.map((it, i) => (
        <Box
          key={it.label}
          sx={{
            px: 2,
            py: 0.9,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: i < items.length - 1 ? `1px solid ${tokens.border}` : 'none',
          }}
        >
          <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{it.label}</Typography>
          <Typography
            sx={{
              fontSize: '0.78rem',
              fontWeight: 600,
              color: it.tone === 'sage' ? tokens.sage : tokens.copperDark,
            }}
          >
            {it.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function FlowGraphVisual() {
  return (
    <Box
      component="svg"
      viewBox="0 0 340 150"
      aria-hidden="true"
      sx={{ width: '100%', maxWidth: 380, display: 'block' }}
    >
      {/* trunk node */}
      <rect x="10" y="58" width="84" height="34" rx="9" fill={tokens.bgDefault} stroke={tokens.border} />
      <text x="52" y="79" textAnchor="middle" fontSize="11" fontWeight="600" fill={tokens.ink} fontFamily="Inter, sans-serif">
        Greeting
      </text>
      {/* edges */}
      <path d="M94 75 C 130 75, 130 30, 165 30" stroke={tokens.copper} strokeWidth="1.4" fill="none" strokeDasharray="4 3" />
      <path d="M94 75 C 130 75, 130 75, 165 75" stroke={tokens.copper} strokeWidth="1.4" fill="none" strokeDasharray="4 3" />
      <path d="M94 75 C 130 75, 130 120, 165 120" stroke={tokens.copper} strokeWidth="1.4" fill="none" strokeDasharray="4 3" />
      {/* intent nodes */}
      <rect x="165" y="13" width="92" height="32" rx="9" fill="rgba(199,91,57,0.10)" stroke="rgba(199,91,57,0.35)" />
      <text x="211" y="33" textAnchor="middle" fontSize="10.5" fontWeight="600" fill={tokens.copperDark} fontFamily="Inter, sans-serif">
        Book a table
      </text>
      <rect x="165" y="59" width="92" height="32" rx="9" fill="rgba(199,91,57,0.10)" stroke="rgba(199,91,57,0.35)" />
      <text x="211" y="79" textAnchor="middle" fontSize="10.5" fontWeight="600" fill={tokens.copperDark} fontFamily="Inter, sans-serif">
        Menu question
      </text>
      <rect x="165" y="104" width="92" height="32" rx="9" fill="rgba(199,91,57,0.10)" stroke="rgba(199,91,57,0.35)" />
      <text x="211" y="124" textAnchor="middle" fontSize="10.5" fontWeight="600" fill={tokens.copperDark} fontFamily="Inter, sans-serif">
        Complaint
      </text>
      {/* outcome */}
      <path d="M257 29 C 285 29, 285 60, 300 67" stroke={tokens.sage} strokeWidth="1.4" fill="none" />
      <circle cx="308" cy="72" r="14" fill="rgba(47,93,80,0.10)" stroke={SAGE_BORDER} />
      <path d="M302 72 l4 4 l8 -8" stroke={tokens.sage} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </Box>
  );
}

function TrendsVisual() {
  return (
    <Box sx={{ width: '100%', maxWidth: 380 }}>
      <Box
        sx={{
          borderRadius: `${tokens.radiusControl}px`,
          border: `1px solid ${tokens.border}`,
          bgcolor: tokens.bgPaper,
          p: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary' }}>
            Sarah M. — engagement & mood
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: tokens.sage }}>
            warming up
          </Typography>
        </Box>
        <Box component="svg" viewBox="0 0 300 70" aria-hidden="true" sx={{ width: '100%', display: 'block' }}>
          <line x1="0" y1="60" x2="300" y2="60" stroke={tokens.border} strokeWidth="1" />
          <path
            d="M0 52 C 30 50, 45 44, 70 46 C 100 48, 115 30, 150 32 C 185 34, 200 18, 240 16 C 265 15, 280 10, 300 8"
            stroke={tokens.copper}
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M0 56 C 40 56, 60 50, 95 50 C 130 50, 150 40, 190 36 C 230 32, 255 26, 300 22"
            stroke={tokens.sage}
            strokeWidth="2"
            strokeDasharray="5 4"
            fill="none"
          />
          <circle cx="240" cy="16" r="3.5" fill={tokens.copper} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          <Typography sx={{ fontSize: '0.68rem', color: tokens.copper, fontWeight: 600 }}>
            — engagement
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', color: tokens.sage, fontWeight: 600 }}>
            - - mood
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

/* ── cards data ───────────────────────────────────────────────── */

const cards = [
  {
    tag: 'CleoleadAI',
    icon: Sparkles,
    gridSize: 7,
    title: 'An assistant that knows your whole business',
    description:
      'CleoleadAI reads everything — conversations, bookings, knowledge base, stats — and answers like a chief of staff. Chat to control the business: ask for reports, upload files straight into the knowledge base, get a daily briefing, or tell it to make calls.',
    bullets: [
      'Knows every conversation, booking, and document',
      'Chat to act: reports, updates, outbound calls',
      'Drop in files — they land in the AI\'s knowledge',
      'Daily briefings, on Telegram or in the app',
    ],
    visual: <HermesChat />,
  },
  {
    tag: 'AI Analyst',
    icon: BarChart3,
    gridSize: 5,
    title: 'A daily analyst on payroll',
    description:
      'Every morning the AI Analyst scores yesterday\'s conversations, flags what customers asked that your AI couldn\'t answer, and tells you exactly what to do next.',
    bullets: [
      'Daily briefing + conversation health stats',
      'Clear what-to-do-next list',
      'Self-learning: daily prompt suggestions you apply in one click',
    ],
    visual: <AnalystBrief />,
  },
  {
    tag: 'Flow Graph',
    icon: GitBranch,
    gridSize: 5,
    title: 'See exactly how your AI thinks',
    description:
      'A live visual map of your AI\'s brain — every intent, every question it asks, every branch. Edit the flow on the canvas and the AI follows it.',
    bullets: [
      'Visual intents & questions editor',
      'Built automatically from real conversations',
      'Change a branch, change the behavior',
    ],
    visual: <FlowGraphVisual />,
  },
  {
    tag: 'Client Trends',
    icon: Users,
    gridSize: 7,
    title: 'Profiles that remember, timelines that warn you',
    description:
      'Every contact gets an auto-enriched profile — interests, key facts, budget hints — plus Trends: engagement and mood timelines, so you see who\'s warming up and who\'s about to walk before it happens.',
    bullets: [
      'Auto-built profile for every contact',
      'Engagement & mood timelines per client',
      'AI-extracted key facts and purchase intent',
    ],
    visual: <TrendsVisual />,
  },
];

const strip = [
  {
    icon: UserCog,
    title: 'Team & roles',
    text: 'Invite your team, assign conversations, control who sees what.',
  },
  {
    icon: MonitorPlay,
    title: 'Demo pages',
    text: 'Send prospects a live, branded demo of your AI — one link, no setup.',
  },
  {
    icon: MessageSquareText,
    title: 'Web chat widget',
    text: 'The same AI on your website — themed to your brand, installed with one script tag.',
  },
];

export default function CommandCenterSection() {
  return (
    <Box id="command-center" sx={{ py: { xs: 8, md: 13 } }}>
      <Container maxWidth="lg">
        <Reveal>
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 7 } }}>
            <Typography variant="overline" sx={{ display: 'block', mb: 1.5 }}>
              Owner Tools
            </Typography>
            <Typography variant="h2" sx={{ mb: 2 }}>
              You stay in command
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', maxWidth: 560, mx: 'auto' }}
            >
              An AI that works for you is only useful if you can see it, steer
              it, and ask it anything. CleoLead gives you the cockpit.
            </Typography>
          </Box>
        </Reveal>

        <Grid container spacing={3}>
          {cards.map((card, index) => {
            const Icon = card.icon;
            return (
              <Grid key={card.tag} size={{ xs: 12, md: card.gridSize }} sx={{ display: 'flex' }}>
                <Reveal
                  delay={(index % 2) * tokens.motion.stagger}
                  sx={{ display: 'flex', width: '100%' }}
                >
                  <Box component="article" sx={cardSx}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={2}
                      sx={{ mb: 3 }}
                    >
                      <Box sx={iconSquareSx}>
                        <Icon size={22} strokeWidth={1.5} />
                      </Box>
                      <Chip
                        label={card.tag}
                        size="small"
                        color="secondary"
                        sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                      />
                    </Stack>
                    <Typography variant="h3" sx={{ fontSize: '1.45rem', mb: 1.5 }}>
                      {card.title}
                    </Typography>
                    <Typography
                      sx={{ fontSize: '0.95rem', lineHeight: 1.65, color: 'text.secondary', mb: 3 }}
                    >
                      {card.description}
                    </Typography>
                    <Box>
                      {card.bullets.map((b) => (
                        <Box
                          key={b}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            py: 1.2,
                            borderTop: `1px solid ${tokens.border}`,
                          }}
                        >
                          <Check
                            size={17}
                            strokeWidth={1.5}
                            color={tokens.copper}
                            style={{ flexShrink: 0, marginTop: 2 }}
                          />
                          <Typography
                            sx={{
                              fontSize: '0.9rem',
                              fontWeight: 500,
                              color: 'text.primary',
                              lineHeight: 1.5,
                            }}
                          >
                            {b}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ mt: 'auto', pt: 3 }}>{card.visual}</Box>
                  </Box>
                </Reveal>
              </Grid>
            );
          })}
        </Grid>

        {/* compact strip: team, demo pages, widget */}
        <Grid container spacing={3} alignItems="stretch" sx={{ mt: { xs: 3, md: 4 } }}>
          {strip.map((s, i) => {
            const Icon = s.icon;
            return (
              <Grid key={s.title} size={{ xs: 12, sm: 4 }} sx={{ display: 'flex' }}>
                <Reveal
                  delay={i * tokens.motion.stagger}
                  sx={{ display: 'flex', width: '100%' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.8,
                      p: 2.5,
                      width: '100%',
                      height: '100%',
                      borderRadius: `${tokens.radiusCard}px`,
                      border: `1px solid ${tokens.border}`,
                      bgcolor: tokens.bgDefault,
                    }}
                  >
                    <Box sx={{ color: tokens.copper, mt: 0.1, flexShrink: 0 }}>
                      <Icon size={20} strokeWidth={1.5} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.92rem', mb: 0.4 }}>
                        {s.title}
                      </Typography>
                      <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', lineHeight: 1.5 }}>
                        {s.text}
                      </Typography>
                    </Box>
                  </Box>
                </Reveal>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
}
